/**
 * Workforce Certifications Page
 *
 * Manages certification lifecycle for workforce members including:
 * - Certification tracking with state machine visualization
 * - Expiration alerts and renewal management
 * - Certification history timeline
 * - Bulk operations for renewals
 */

import React, { useState, useEffect } from 'react';
import {
  Award,
  Plus,
  Download,
  Palette,
  Search,
  Filter,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronRight,
  X,
  User,
  FileText,
  History,
  Bell,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

interface CertificationType {
  id: number;
  name: string;
  code: string;
  description: string;
  issuing_body: string;
  validity_months: number;
  renewal_window_days: number;
  required_for_roles: string[];
}

interface Certification {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  certification_type_id: number;
  certification_name: string;
  certification_code: string;
  status: string;
  issue_date: string | null;
  expiration_date: string | null;
  certificate_number: string | null;
  days_until_expiration: number | null;
  created_at: string;
  training_progress_pct?: number | null;
  linked_course_id?: number | null;
  linked_course_title?: string | null;
}

interface CertificationHistory {
  id: number;
  certification_id: number;
  old_status: string;
  new_status: string;
  changed_by: string;
  notes: string;
  created_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any; bgColor: string }> = {
  enrolled: { label: 'Enrolled', color: 'text-blue-700', icon: User, bgColor: 'bg-blue-100' },
  in_progress: { label: 'In Progress', color: 'text-purple-700', icon: Clock, bgColor: 'bg-purple-100' },
  exam_ready: { label: 'Exam Ready', color: 'text-indigo-700', icon: FileText, bgColor: 'bg-indigo-100' },
  exam_scheduled: { label: 'Exam Scheduled', color: 'text-amber-700', icon: Calendar, bgColor: 'bg-amber-100' },
  certified: { label: 'Certified', color: 'text-emerald-700', icon: CheckCircle, bgColor: 'bg-emerald-100' },
  renewal_due: { label: 'Renewal Due', color: 'text-orange-700', icon: RefreshCw, bgColor: 'bg-orange-100' },
  expired: { label: 'Expired', color: 'text-red-700', icon: XCircle, bgColor: 'bg-red-100' },
};

interface NewCertificationForm {
  user_id: string;
  certification_type_id: string;
}

interface NewCertificationTypeForm {
  code: string;
  name: string;
  description: string;
  required_training_hours: string;
  requires_exam: boolean;
  passing_score: string;
  validity_months: string;
  renewal_reminder_days: string;
  accrediting_body: string;
  state_recognized: boolean;
  recognized_states: string[];
}

interface Curriculum {
  id: number;
  name: string;
  curriculum_type: string;
  total_hours: number;
}

// Manage Certification Types Modal Component
function ManageCertificationTypesModal({
  isOpen,
  onClose,
  certificationTypes,
  onTypeCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  certificationTypes: CertificationType[];
  onTypeCreated: () => void;
}) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedType, setSelectedType] = useState<CertificationType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [newType, setNewType] = useState<NewCertificationTypeForm>({
    code: '',
    name: '',
    description: '',
    required_training_hours: '',
    requires_exam: true,
    passing_score: '80',
    validity_months: '24',
    renewal_reminder_days: '60',
    accrediting_body: '',
    state_recognized: false,
    recognized_states: [],
  });

  useEffect(() => {
    if (isOpen) {
      fetchCurricula();
    }
  }, [isOpen]);

  const fetchCurricula = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl('/api/workforce/curricula'));

      if (res.ok) {
        const data = await res.json();
        setCurricula(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching curricula:', error);
    }
  };

  const handleCreateType = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = getAuthToken();
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/certifications/types'), {
        method: 'POST',
        jsonBody: {
          code: newType.code,
          name: newType.name,
          description: newType.description || null,
          required_training_hours: newType.required_training_hours ? parseInt(newType.required_training_hours) : null,
          requires_exam: newType.requires_exam,
          passing_score: parseInt(newType.passing_score),
          validity_months: parseInt(newType.validity_months),
          renewal_reminder_days: parseInt(newType.renewal_reminder_days),
          accrediting_body: newType.accrediting_body || null,
          state_recognized: newType.state_recognized,
          recognized_states: newType.recognized_states,
        },
      });

      if (response.ok) {
        setShowCreateForm(false);
        setNewType({
          code: '',
          name: '',
          description: '',
          required_training_hours: '',
          requires_exam: true,
          passing_score: '80',
          validity_months: '24',
          renewal_reminder_days: '60',
          accrediting_body: '',
          state_recognized: false,
          recognized_states: [],
        });
        onTypeCreated();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to create certification type';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Error creating certification type:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-v2border">
            <h2 className="text-lg font-semibold text-ink">
              {showCreateForm ? 'Create Certification Type' : selectedType ? 'Certification Type Details' : 'Manage Certification Types'}
            </h2>
            <button
              onClick={() => {
                if (showCreateForm) {
                  setShowCreateForm(false);
                } else if (selectedType) {
                  setSelectedType(null);
                } else {
                  onClose();
                }
              }}
              className="p-2 hover:bg-paper-2 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* List View */}
            {!showCreateForm && !selectedType && (
              <div className="space-y-4">
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full px-4 py-3 border-2 border-dashed border-v2border rounded-lg text-ink-500 hover:border-emerald-400 hover:text-emerald-600 flex items-center justify-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span>Create New Certification Type</span>
                </button>

                {certificationTypes.length === 0 ? (
                  <div className="text-center py-8">
                    <Award className="w-12 h-12 text-ink-400 mx-auto mb-3" />
                    <p className="text-ink-500">No certification types defined yet</p>
                    <p className="text-sm text-ink-500 mt-1">Create your first type to start issuing certifications</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {certificationTypes.map((type) => (
                      <div
                        key={type.id}
                        onClick={() => setSelectedType(type)}
                        className="p-4 border border-v2border rounded-lg hover:border-emerald-300 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="font-medium text-ink">{type.name}</h3>
                              <span className="text-xs bg-paper-2 text-ink-500 px-2 py-0.5 rounded">{type.code}</span>
                            </div>
                            <p className="text-sm text-ink-500 mt-1 line-clamp-2">{type.description || 'No description'}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-ink-400 flex-shrink-0" />
                        </div>
                        <div className="flex items-center space-x-4 mt-3 text-xs text-ink-500">
                          <span>Valid: {type.validity_months} months</span>
                          {type.required_for_roles?.length > 0 && (
                            <span>Required for {type.required_for_roles.length} roles</span>
                          )}
                          {type.issuing_body && <span>Issued by: {type.issuing_body}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Detail View */}
            {selectedType && !showCreateForm && (
              <div className="space-y-6">
                <div className="bg-paper rounded-xl p-4 space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-3 bg-emerald-100 rounded-lg">
                      <Award className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-ink">{selectedType.name}</h3>
                      <p className="text-sm text-ink-500">Code: {selectedType.code}</p>
                    </div>
                  </div>
                  {selectedType.description && (
                    <p className="text-ink-500">{selectedType.description}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 uppercase font-medium">Validity Period</p>
                    <p className="text-lg font-semibold text-blue-900">{selectedType.validity_months} months</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 uppercase font-medium">Renewal Reminder</p>
                    <p className="text-lg font-semibold text-amber-900">{selectedType.renewal_window_days} days before</p>
                  </div>
                </div>

                {selectedType.issuing_body && (
                  <div>
                    <h4 className="text-sm font-medium text-ink-700 mb-2">Accrediting Body</h4>
                    <p className="text-ink">{selectedType.issuing_body}</p>
                  </div>
                )}

                {selectedType.required_for_roles && selectedType.required_for_roles.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-ink-700 mb-2">Required for Roles</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedType.required_for_roles.map((role, idx) => (
                        <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-v2border">
                  <button
                    onClick={() => setSelectedType(null)}
                    className="w-full px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                  >
                    Back to List
                  </button>
                </div>
              </div>
            )}

            {/* Create Form */}
            {showCreateForm && (
              <form onSubmit={handleCreateType} className="space-y-4">
                {submitError && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {submitError}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Code *</label>
                    <input
                      type="text"
                      required
                      value={newType.code}
                      onChange={(e) => setNewType({ ...newType, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., CHW, HAA"
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={newType.name}
                      onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                      placeholder="e.g., Community Health Worker"
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Description</label>
                  <textarea
                    value={newType.description}
                    onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                    placeholder="Describe this certification type and its requirements..."
                    rows={3}
                    className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                <div className="bg-paper rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-ink">Training Requirements</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Required Training Hours</label>
                      <input
                        type="number"
                        value={newType.required_training_hours}
                        onChange={(e) => setNewType({ ...newType, required_training_hours: e.target.value })}
                        placeholder="e.g., 80"
                        min="0"
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Accrediting Body</label>
                      <input
                        type="text"
                        value={newType.accrediting_body}
                        onChange={(e) => setNewType({ ...newType, accrediting_body: e.target.value })}
                        placeholder="e.g., State Health Dept"
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newType.requires_exam}
                        onChange={(e) => setNewType({ ...newType, requires_exam: e.target.checked })}
                        className="rounded border-v2border text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-ink-700">Requires Exam</span>
                    </label>
                    {newType.requires_exam && (
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-ink-700">Passing Score:</label>
                        <input
                          type="number"
                          value={newType.passing_score}
                          onChange={(e) => setNewType({ ...newType, passing_score: e.target.value })}
                          min="0"
                          max="100"
                          className="w-20 px-2 py-1 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                        />
                        <span className="text-sm text-ink-500">%</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-paper rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-ink">Validity & Renewal</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Validity (months) *</label>
                      <input
                        type="number"
                        required
                        value={newType.validity_months}
                        onChange={(e) => setNewType({ ...newType, validity_months: e.target.value })}
                        min="1"
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">Renewal Reminder (days)</label>
                      <input
                        type="number"
                        value={newType.renewal_reminder_days}
                        onChange={(e) => setNewType({ ...newType, renewal_reminder_days: e.target.value })}
                        min="1"
                        className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-paper rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-ink">State Recognition</h4>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newType.state_recognized}
                        onChange={(e) => setNewType({ ...newType, state_recognized: e.target.checked, recognized_states: e.target.checked ? newType.recognized_states : [] })}
                        className="rounded border-v2border text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-ink-700">State Recognized</span>
                    </label>
                  </div>

                  {newType.state_recognized && (
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-2">Recognized States</label>
                      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                        {US_STATES.map((state) => (
                          <label
                            key={state}
                            className={`px-2 py-1 rounded text-sm cursor-pointer transition-colors ${
                              newType.recognized_states.includes(state)
                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                : 'bg-paper-2 text-ink-500 border border-v2border hover:border-emerald-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={newType.recognized_states.includes(state)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewType({ ...newType, recognized_states: [...newType.recognized_states, state] });
                                } else {
                                  setNewType({ ...newType, recognized_states: newType.recognized_states.filter(s => s !== state) });
                                }
                              }}
                            />
                            {state}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                  >
                    {submitting ? 'Creating...' : 'Create Type'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WorkforceCertificationsPage() {
  const { t } = useAppTranslation();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [certificationTypes, setCertificationTypes] = useState<CertificationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCert, setSelectedCert] = useState<Certification | null>(null);
  const [certHistory, setCertHistory] = useState<CertificationHistory[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showDesign, setShowDesign] = useState(false);
  const [newCert, setNewCert] = useState<NewCertificationForm>({ user_id: '', certification_type_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [users, setUsers] = useState<{ id: number; name: string; email: string }[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for certifications page');
      setLoading(false);
      return;
    }

    try {
      const [certsRes, typesRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/certifications')),
        authFetch(getWeaveinApiUrl('/api/workforce/certifications/types')),
      ]);

      if (certsRes.ok) {
        const data = await certsRes.json();
        setCertifications(data.items || []);
      }

      if (typesRes.ok) {
        const data = await typesRes.json();
        setCertificationTypes(data || []);
      }
    } catch (error) {
      console.error('Error fetching certifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertHistory = async (certId: number) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/certifications/${certId}/history`));

      if (res.ok) {
        const data = await res.json();
        setCertHistory(data || []);
      }
    } catch (error) {
      console.error('Error fetching certification history:', error);
    }
  };

  const handleSelectCert = (cert: Certification) => {
    setSelectedCert(cert);
    fetchCertHistory(cert.id);
  };

  const handleStatusTransition = async (certId: number, newStatus: string, notes?: string) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      // State-machine transition endpoint is PUT /{id}/status with {status, reason}
      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/certifications/${certId}/status`), {
        method: 'PUT',
        jsonBody: { status: newStatus, reason: notes },
      });

      if (res.ok) {
        const updated = await res.json().catch(() => null);
        fetchData();
        if (selectedCert?.id === certId) {
          // merge just the status so the panel's enriched fields (user_name…) survive
          if (updated?.status) setSelectedCert((prev) => (prev ? { ...prev, status: updated.status } : prev));
          fetchCertHistory(certId);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        alert(typeof err.detail === 'string' ? err.detail : 'Could not update certification status');
      }
    } catch (error) {
      console.error('Error transitioning certification status:', error);
      alert('Could not update certification status');
    }
  };

  const handleSendReminder = async (certId: number) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/certifications/${certId}/remind`), {
        method: 'POST',
      });

      alert(res.ok ? 'Renewal reminder sent successfully' : 'Could not send reminder');
    } catch (error) {
      console.error('Error sending reminder:', error);
    }
  };

  // Download the certificate PDF (authed endpoint -> blob -> save).
  const downloadCertificate = async (cert: Certification) => {
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/certifications/${cert.id}/certificate`));
      if (!res.ok) {
        alert('Could not generate the certificate.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(cert.user_name || 'certificate').replace(/\s+/g, '_')}_certificate.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading certificate:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const activeCboId = localStorage.getItem('active_cbo_id');

      // First try to get all organization users (preferred - includes all users in org)
      if (activeCboId) {
        const orgUsersRes = await authFetch(getWeaveinApiUrl(`/api/cbos/${activeCboId}/users`));

        if (orgUsersRes.ok) {
          const orgUsers = await orgUsersRes.json();
          if (orgUsers && orgUsers.length > 0) {
            setUsers(orgUsers.map((u: any) => ({
              id: u.id || u.user_id,
              name: u.name || u.user_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
              email: u.email || u.user_email,
            })));
            return;
          }
        }
      }

      // Fallback to roster users if org users endpoint fails or returns empty
      const res = await authFetch(getWeaveinApiUrl('/api/workforce/roster?limit=200'));

      if (res.ok) {
        const data = await res.json();
        setUsers(data.items?.map((r: any) => ({
          id: r.user_id,
          name: r.user_name,
          email: r.user_email,
        })) || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleOpenAddModal = () => {
    fetchUsers();
    setShowAddModal(true);
  };

  const handleIssueCertification = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = getAuthToken();
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/certifications'), {
        method: 'POST',
        jsonBody: {
          user_id: parseInt(newCert.user_id),
          certification_type_id: parseInt(newCert.certification_type_id),
        },
      });

      if (response.ok) {
        setShowAddModal(false);
        setNewCert({ user_id: '', certification_type_id: '' });
        fetchData();
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (422) which return detail as an array
        let errorMessage = 'Failed to issue certification';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        setSubmitError(errorMessage);
      }
    } catch (error) {
      console.error('Error issuing certification:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCertifications = certifications.filter(cert => {
    const matchesSearch =
      cert.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certification_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: certifications.length,
    certified: certifications.filter(c => c.status === 'certified').length,
    expiringSoon: certifications.filter(c => c.days_until_expiration !== null && c.days_until_expiration <= 60 && c.days_until_expiration > 0).length,
    expired: certifications.filter(c => c.status === 'expired').length,
    inProgress: certifications.filter(c => ['enrolled', 'in_progress', 'exam_ready', 'exam_scheduled'].includes(c.status)).length,
  };

  const getNextActions = (status: string): { label: string; status: string }[] => {
    const transitions: Record<string, { label: string; status: string }[]> = {
      enrolled: [{ label: 'Start Training', status: 'in_progress' }],
      in_progress: [{ label: 'Mark Exam Ready', status: 'exam_ready' }],
      exam_ready: [{ label: 'Schedule Exam', status: 'exam_scheduled' }],
      exam_scheduled: [
        { label: 'Mark Certified', status: 'certified' },
        { label: 'Reschedule', status: 'exam_ready' },
      ],
      certified: [],
      renewal_due: [{ label: 'Complete Renewal', status: 'certified' }],
      expired: [{ label: 'Re-enroll', status: 'enrolled' }],
    };
    return transitions[status] || [];
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Certifications"
        title={t('workforce.certifications.title', 'Certifications')}
        description={t('workforce.certifications.description', 'Track and manage workforce certifications')}
        actions={
          <>
            <button
              onClick={() => setShowTypeModal(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <FileText className="w-4 h-4" />
              <span>{t('workforce.certifications.manageTypes', 'Manage Types')}</span>
            </button>
            <button
              onClick={() => setShowDesign(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <Palette className="w-4 h-4" />
              <span>Certificate design</span>
            </button>
            <button
              onClick={handleOpenAddModal}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              <span>{t('workforce.certifications.issueCertification', 'Issue Certification')}</span>
            </button>
          </>
        }
      />
      <PageContainer>
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <p className="text-sm text-ink-500">{t('workforce.certifications.stats.total', 'Total')}</p>
            <p className="font-display text-2xl font-semibold text-ink">{stats.total}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <p className="text-sm text-ink-500">{t('workforce.certifications.stats.certified', 'Certified')}</p>
            <p className="font-display text-2xl font-semibold text-emerald-600">{stats.certified}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <p className="text-sm text-ink-500">{t('workforce.certifications.stats.inProgress', 'In Progress')}</p>
            <p className="font-display text-2xl font-semibold text-purple-600">{stats.inProgress}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <p className="text-sm text-ink-500">{t('workforce.certifications.stats.expiringSoon', 'Expiring Soon')}</p>
            <p className="font-display text-2xl font-semibold text-orange-600">{stats.expiringSoon}</p>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <p className="text-sm text-ink-500">{t('workforce.certifications.stats.expired', 'Expired')}</p>
            <p className="font-display text-2xl font-semibold text-red-600">{stats.expired}</p>
          </div>
        </div>

        {/* Expiring Soon Alert */}
        {stats.expiringSoon > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    {t('workforce.certifications.expiringAlert', '{{count}} certification(s) expiring within 60 days', { count: stats.expiringSoon })}
                  </p>
                  <p className="text-sm text-amber-600">{t('workforce.certifications.sendReminders', 'Send renewal reminders to keep workforce compliant')}</p>
                </div>
              </div>
              <button
                onClick={() => setStatusFilter('renewal_due')}
                className="text-amber-700 hover:text-amber-800 font-medium text-sm flex items-center"
              >
                {t('workforce.certifications.viewAll', 'View All')} <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
            <input
              type="text"
              placeholder={t('workforce.certifications.searchPlaceholder', 'Search by name, certification, or certificate number...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="all">{t('workforce.certifications.allStatuses', 'All Statuses')}</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>

        {/* Certifications Table */}
        <div className="bg-surface rounded-xl border border-v2border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-paper border-b border-v2border">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.certifications.table.person', 'Person')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.certifications.table.certification', 'Certification')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.certifications.table.status', 'Status')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.certifications.table.issueDate', 'Issue Date')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.certifications.table.expiration', 'Expiration')}</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">{t('workforce.certifications.table.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-ink-500">
                      {t('workforce.certifications.loading', 'Loading certifications...')}
                    </td>
                  </tr>
                ) : filteredCertifications.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Award className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                      <p className="text-ink-500">{t('workforce.certifications.noCertifications', 'No certifications found')}</p>
                    </td>
                  </tr>
                ) : (
                  filteredCertifications.map((cert) => {
                    const statusConfig = STATUS_CONFIG[cert.status] || STATUS_CONFIG.enrolled;
                    const StatusIcon = statusConfig.icon;

                    return (
                      <tr
                        key={cert.id}
                        className="hover:bg-paper cursor-pointer"
                        onClick={() => handleSelectCert(cert)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-paper-2 flex items-center justify-center">
                              <span className="text-ink-500 font-medium">
                                {cert.user_name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-ink">{cert.user_name}</p>
                              <p className="text-sm text-ink-500">{cert.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-ink">{cert.certification_name}</p>
                          <p className="text-sm text-ink-500">{cert.certificate_number || 'Pending'}</p>
                          {cert.linked_course_id != null && (
                            <div className="mt-1.5 w-40" title={cert.linked_course_title || 'Training progress'}>
                              <div className="flex items-center justify-between text-[10px] text-ink-400 mb-0.5">
                                <span>Training</span>
                                <span>{Math.round(cert.training_progress_pct || 0)}%</span>
                              </div>
                              <div className="h-1.5 bg-paper-2 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-emerald-500 rounded-full"
                                  style={{ width: `${Math.min(100, cert.training_progress_pct || 0)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-ink-500">
                          {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4">
                          {cert.expiration_date ? (
                            <div>
                              <p className={`text-sm font-medium ${
                                cert.days_until_expiration !== null && cert.days_until_expiration <= 30
                                  ? 'text-red-600'
                                  : cert.days_until_expiration !== null && cert.days_until_expiration <= 60
                                  ? 'text-amber-600'
                                  : 'text-ink-500'
                              }`}>
                                {new Date(cert.expiration_date).toLocaleDateString()}
                              </p>
                              {cert.days_until_expiration !== null && cert.days_until_expiration > 0 && (
                                <p className="text-xs text-ink-500">{cert.days_until_expiration} days left</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-ink-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {cert.status === 'certified' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadCertificate(cert);
                                }}
                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                                title="Download certificate"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                            {cert.status === 'renewal_due' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendReminder(cert.id);
                                }}
                                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                                title="Send reminder"
                              >
                                <Bell className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectCert(cert);
                              }}
                              className="p-1.5 text-ink-500 hover:bg-paper-2 rounded"
                              title="View details"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Certification Detail Slide-out */}
        {selectedCert && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedCert(null)} />
            <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-surface shadow-xl">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-ink">{t('workforce.certifications.details.title', 'Certification Details')}</h2>
                  <button
                    onClick={() => setSelectedCert(null)}
                    className="p-2 hover:bg-paper-2 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Person Info */}
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-emerald-600 font-bold text-xl">
                        {selectedCert.user_name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-ink">{selectedCert.user_name}</h3>
                      <p className="text-ink-500">{selectedCert.user_email}</p>
                    </div>
                  </div>

                  {/* Certification Info */}
                  <div className="bg-paper rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ink-500">{t('workforce.certifications.details.certification', 'Certification')}</span>
                      <span className="font-medium text-ink">{selectedCert.certification_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ink-500">{t('workforce.certifications.details.certificateNumber', 'Certificate #')}</span>
                      <span className="font-medium text-ink">{selectedCert.certificate_number || t('workforce.certifications.details.pending', 'Pending')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ink-500">{t('workforce.certifications.details.status', 'Status')}</span>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedCert.status]?.bgColor} ${STATUS_CONFIG[selectedCert.status]?.color}`}>
                        {STATUS_CONFIG[selectedCert.status]?.label}
                      </span>
                    </div>
                    {selectedCert.issue_date && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-ink-500">{t('workforce.certifications.details.issueDate', 'Issue Date')}</span>
                        <span className="font-medium text-ink">
                          {new Date(selectedCert.issue_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {selectedCert.expiration_date && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-ink-500">{t('workforce.certifications.details.expirationDate', 'Expiration Date')}</span>
                        <span className={`font-medium ${
                          selectedCert.days_until_expiration !== null && selectedCert.days_until_expiration <= 30
                            ? 'text-red-600'
                            : 'text-ink'
                        }`}>
                          {new Date(selectedCert.expiration_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Training progress (from the linked LMS course) */}
                  {selectedCert.linked_course_id != null && (
                    <div>
                      <h4 className="font-medium text-ink mb-2">Training progress</h4>
                      <div className="flex items-center justify-between text-sm text-ink-500 mb-1">
                        <span>{selectedCert.linked_course_title || 'Linked course'}</span>
                        <span className="font-medium text-ink">{Math.round(selectedCert.training_progress_pct || 0)}%</span>
                      </div>
                      <div className="h-2.5 bg-paper-2 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${Math.min(100, selectedCert.training_progress_pct || 0)}%` }}
                        />
                      </div>
                      <p className="text-xs text-ink-400 mt-1">
                        Advances the certification automatically as the learner completes the course.
                      </p>
                    </div>
                  )}

                  {/* State Machine Visualization */}
                  <div>
                    <h4 className="font-medium text-ink mb-3">{t('workforce.certifications.details.progress', 'Certification Progress')}</h4>
                    <div className="flex items-center space-x-1 overflow-x-auto pb-2">
                      {Object.entries(STATUS_CONFIG).map(([key, config], index) => {
                        const StatusIcon = config.icon;
                        const isCurrent = selectedCert.status === key;
                        const isPast = Object.keys(STATUS_CONFIG).indexOf(selectedCert.status) > index;

                        return (
                          <React.Fragment key={key}>
                            <div className={`flex flex-col items-center min-w-[60px] ${
                              isCurrent ? 'opacity-100' : isPast ? 'opacity-60' : 'opacity-30'
                            }`}>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isCurrent ? config.bgColor : isPast ? 'bg-paper-2' : 'bg-paper-2'
                              }`}>
                                <StatusIcon className={`w-4 h-4 ${isCurrent ? config.color : 'text-ink-400'}`} />
                              </div>
                              <span className="text-[10px] text-ink-500 mt-1 text-center">{config.label}</span>
                            </div>
                            {index < Object.keys(STATUS_CONFIG).length - 1 && (
                              <div className={`w-4 h-0.5 ${isPast ? 'bg-gray-300' : 'bg-paper-2'}`} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  {getNextActions(selectedCert.status).length > 0 && (
                    <div>
                      <h4 className="font-medium text-ink mb-3">{t('workforce.certifications.details.availableActions', 'Available Actions')}</h4>
                      <div className="space-y-2">
                        {getNextActions(selectedCert.status).map((action) => (
                          <button
                            key={action.status}
                            onClick={() => handleStatusTransition(selectedCert.id, action.status)}
                            className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center space-x-2"
                          >
                            <span>{action.label}</span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* History Timeline */}
                  <div>
                    <h4 className="font-medium text-ink mb-3 flex items-center space-x-2">
                      <History className="w-4 h-4" />
                      <span>{t('workforce.certifications.details.history', 'History')}</span>
                    </h4>
                    {certHistory.length === 0 ? (
                      <p className="text-sm text-ink-500">{t('workforce.certifications.details.noHistory', 'No history available')}</p>
                    ) : (
                      <div className="space-y-3">
                        {certHistory.map((entry) => (
                          <div key={entry.id} className="flex items-start space-x-3">
                            <div className="w-2 h-2 mt-2 rounded-full bg-emerald-500" />
                            <div className="flex-1">
                              <p className="text-sm text-ink">
                                Status changed from{' '}
                                <span className="font-medium">{STATUS_CONFIG[entry.old_status]?.label || entry.old_status}</span>
                                {' '}to{' '}
                                <span className="font-medium">{STATUS_CONFIG[entry.new_status]?.label || entry.new_status}</span>
                              </p>
                              {entry.notes && (
                                <p className="text-sm text-ink-500 mt-1">{entry.notes}</p>
                              )}
                              <p className="text-xs text-ink-400 mt-1">
                                {new Date(entry.created_at).toLocaleString()} by {entry.changed_by}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-v2border flex items-center justify-between">
                  <button className="px-4 py-2 text-ink-700 hover:bg-paper-2 rounded-lg flex items-center space-x-2">
                    <Download className="w-4 h-4" />
                    <span>{t('workforce.certifications.details.downloadCertificate', 'Download Certificate')}</span>
                  </button>
                  {selectedCert.status === 'renewal_due' && (
                    <button
                      onClick={() => handleSendReminder(selectedCert.id)}
                      className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 flex items-center space-x-2"
                    >
                      <Bell className="w-4 h-4" />
                      <span>{t('workforce.certifications.details.sendReminder', 'Send Reminder')}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manage Certification Types Modal */}
        {showTypeModal && (
          <ManageCertificationTypesModal
            isOpen={showTypeModal}
            onClose={() => setShowTypeModal(false)}
            certificationTypes={certificationTypes}
            onTypeCreated={() => {
              fetchData();
            }}
          />
        )}

        {showDesign && <CertificateDesignModal onClose={() => setShowDesign(false)} />}

        {/* Issue Certification Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
              <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-ink">Issue Certification</h2>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 hover:bg-paper-2 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {submitError && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleIssueCertification} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">User *</label>
                    <select
                      required
                      value={newCert.user_id}
                      onChange={(e) => setNewCert({ ...newCert, user_id: e.target.value })}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Select a user...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                    {users.length === 0 && (
                      <p className="text-sm text-ink-500 mt-1">
                        No users found. Add users to the roster first.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Certification Type *</label>
                    <select
                      required
                      value={newCert.certification_type_id}
                      onChange={(e) => setNewCert({ ...newCert, certification_type_id: e.target.value })}
                      className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Select a certification type...</option>
                      {certificationTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name} ({type.code})
                        </option>
                      ))}
                    </select>
                    {certificationTypes.length === 0 && (
                      <p className="text-sm text-ink-500 mt-1">
                        No certification types defined. Create types first.
                      </p>
                    )}
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || users.length === 0 || certificationTypes.length === 0}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                    >
                      {submitting ? 'Issuing...' : 'Issue Certification'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      </PageContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Certificate design editor — the org's reusable certificate template
// (logo, accent color, title, signature). Saved via the certificate-templates
// API; the default is what issued certificates render with.
// ---------------------------------------------------------------------------
interface CertTemplate {
  id?: number;
  name: string;
  title: string;
  accent_color: string;
  logo_url: string;
  signature_name: string;
  signature_title: string;
  org_name_override: string;
  is_default: boolean;
}

function CertificateDesignModal({ onClose }: { onClose: () => void }) {
  const blank: CertTemplate = {
    name: 'Default certificate',
    title: 'Certificate of Completion',
    accent_color: '#C9A227',
    logo_url: '',
    signature_name: '',
    signature_title: '',
    org_name_override: '',
    is_default: true,
  };
  const [tpl, setTpl] = useState<CertTemplate>(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(getWeaveinApiUrl('/api/workforce/certifications/certificate-templates'));
        if (res.ok) {
          const data = await res.json();
          const def = (data.templates || []).find((x: any) => x.is_default) || (data.templates || [])[0];
          if (def)
            setTpl({
              id: def.id,
              name: def.name || 'Default certificate',
              title: def.title || 'Certificate of Completion',
              accent_color: def.accent_color || '#C9A227',
              logo_url: def.logo_url || '',
              signature_name: def.signature_name || '',
              signature_title: def.signature_title || '',
              org_name_override: def.org_name_override || '',
              is_default: def.is_default ?? true,
            });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    const url = tpl.id
      ? `/api/workforce/certifications/certificate-templates/${tpl.id}`
      : '/api/workforce/certifications/certificate-templates';
    const res = await authFetch(getWeaveinApiUrl(url), { method: tpl.id ? 'PUT' : 'POST', jsonBody: { ...tpl, is_default: true } });
    setSaving(false);
    setMsg(res.ok ? 'Saved. New certificates use this design.' : 'Could not save the design.');
  };

  const set = (patch: Partial<CertTemplate>) => setTpl((t) => ({ ...t, ...patch }));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-ink">Certificate design</h2>
          <button onClick={onClose} className="p-2 hover:bg-paper-2 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <p className="text-sm text-ink-500 py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-3">
            <Field label="Title">
              <input value={tpl.title} onChange={(e) => set({ title: e.target.value })} className="w-full px-3 py-2 border border-v2border rounded-lg text-sm" />
            </Field>
            <Field label="Organization name (as shown)">
              <input value={tpl.org_name_override} onChange={(e) => set({ org_name_override: e.target.value })} placeholder="Defaults to your org name" className="w-full px-3 py-2 border border-v2border rounded-lg text-sm" />
            </Field>
            <Field label="Logo URL">
              <input value={tpl.logo_url} onChange={(e) => set({ logo_url: e.target.value })} placeholder="https://… or data: URI" className="w-full px-3 py-2 border border-v2border rounded-lg text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Accent color">
                <div className="flex items-center gap-2">
                  <input type="color" value={tpl.accent_color} onChange={(e) => set({ accent_color: e.target.value })} className="h-9 w-12 rounded border border-v2border" />
                  <input value={tpl.accent_color} onChange={(e) => set({ accent_color: e.target.value })} className="flex-1 px-3 py-2 border border-v2border rounded-lg text-sm" />
                </div>
              </Field>
              <div />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Signature name">
                <input value={tpl.signature_name} onChange={(e) => set({ signature_name: e.target.value })} className="w-full px-3 py-2 border border-v2border rounded-lg text-sm" />
              </Field>
              <Field label="Signature title">
                <input value={tpl.signature_title} onChange={(e) => set({ signature_title: e.target.value })} className="w-full px-3 py-2 border border-v2border rounded-lg text-sm" />
              </Field>
            </div>

            {/* Live-ish preview */}
            <div className="mt-2 rounded-lg border p-4" style={{ borderColor: tpl.accent_color }}>
              {tpl.logo_url && <img src={tpl.logo_url} alt="logo" className="max-h-10 mx-auto mb-2" />}
              <p className="text-center text-[11px] uppercase tracking-widest text-ink-500">{tpl.org_name_override || 'Your organization'}</p>
              <p className="text-center text-lg font-semibold text-ink mt-1">{tpl.title}</p>
              <p className="text-center text-xs text-ink-500">This certifies that</p>
              <p className="text-center text-xl font-semibold text-ink my-1">Learner Name</p>
              <p className="text-center text-sm font-semibold" style={{ color: tpl.accent_color }}>Certification</p>
              {tpl.signature_name && (
                <p className="text-center text-xs text-ink-500 mt-3">
                  <span className="block border-t w-40 mx-auto mb-0.5" />
                  {tpl.signature_name}{tpl.signature_title ? ` · ${tpl.signature_title}` : ''}
                </p>
              )}
            </div>

            {msg && <p className="text-sm text-emerald-700">{msg}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="px-4 py-2 border border-v2border rounded-lg">Close</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50">
                {saving ? 'Saving…' : 'Save design'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-ink-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
